import { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail, euro, formatDate, downloadFile } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import DatePickerField from "@/components/DatePickerField";
import MultiSelect from "@/components/MultiSelect";
import Combobox from "@/components/Combobox";
import DataTable from "@/components/DataTable";
import ConfirmDelete from "@/components/ConfirmDelete";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Plus, Pencil, Trash2, ReceiptText, FileUp, FileText, Loader2, X, Sparkles, FileSpreadsheet, Search, ShieldCheck, Scale,
} from "lucide-react";

const TIPI = [
  { value: "ordinaria", label: "Fattura" },
  { value: "svincolo", label: "Svincolo ritenuta" },
  { value: "compensazione", label: "Compensazione BEF" },
];
import { toast } from "sonner";

const emptyAlloc = () => ({ commessa_id: "", importo: "", ritenuta: "", ritenuta_pct: "" });
const empty = {
  numero_fattura: "", data_fattura: "", committente: "",
  numero_contratto: "", numero_ordine: "", numero_ses: "",
  data_emissione: new Date().toISOString().slice(0, 10),
  imponibile: "", iva: "", note: "", is_svincolo: false, is_compensazione: false,
  storage_path: null, file_name: null,
  allocazioni: [emptyAlloc()],
};

export default function Fatture() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [commesse, setCommesse] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [aiWarning, setAiWarning] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);
  const dragCounter = useRef(0);

  // ---------- Coda caricamenti da confermare (in memoria) ----------
  const [pending, setPending] = useState([]);
  const [confirmingPid, setConfirmingPid] = useState(null);

  // ---------- Filtri tabella ----------
  const [query, setQuery] = useState("");
  const [fCommessa, setFCommessa] = useState([]);
  const [fCommittente, setFCommittente] = useState([]);
  const [committenti, setCommittenti] = useState([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [fattFrom, setFattFrom] = useState("");
  const [fattTo, setFattTo] = useState("");
  const [fTipo, setFTipo] = useState([]);
  const hasFilter = !!query || fCommessa.length > 0 || fCommittente.length > 0 || fTipo.length > 0 || !!dateFrom || !!dateTo || !!fattFrom || !!fattTo;
  const resetFilters = () => { setQuery(""); setFCommessa([]); setFCommittente([]); setFTipo([]); setDateFrom(""); setDateTo(""); setFattFrom(""); setFattTo(""); };

  const load = () => api.get("/fatture").then((r) => setItems(r.data));
  const loadBozze = () => api.get("/fatture/bozze").then((r) => setPending(
    r.data.map((b) => ({ pid: b.id, bozzaId: b.id, blobUrl: null, warning: b.warning || null, form: b.form ? { ...empty, ...b.form } : buildForm(b) }))
  )).catch(() => {});
  useEffect(() => {
    load();
    loadBozze();
    api.get("/commesse").then((r) => setCommesse(r.data));
    api.get("/committenti").then((r) => setCommittenti(r.data)).catch(() => {});
  }, []);

  // Apertura automatica della maschera fattura quando si arriva da ?fattura=<id> (es. da scheda commessa)
  const [searchParams, setSearchParams] = useSearchParams();
  const autoOpenedRef = useRef(false);
  useEffect(() => {
    const fid = searchParams.get("fattura");
    if (fid && items.length && !autoOpenedRef.current) {
      const f = items.find((x) => x.id === fid);
      if (f) { autoOpenedRef.current = true; openEdit(f); setSearchParams({}, { replace: true }); }
    }
  }, [items, searchParams]); // eslint-disable-line

  const revokePdf = () => { if (pdfUrl) { URL.revokeObjectURL(pdfUrl); } };

  const commessaLabel = (id) => {
    const c = commesse.find((x) => x.id === id);
    return c ? `${c.codice} · ${c.nome}` : "—";
  };

  // Commesse assegnabili: sottocommesse + commesse senza sottocommesse (le madri con figli non sono assegnabili)
  const allocatable = (() => {
    const parents = commesse.filter((c) => !c.parent_id);
    const out = [];
    parents.forEach((p) => {
      const kids = commesse.filter((c) => c.parent_id === p.id);
      if (kids.length) {
        kids.forEach((k) => {
          const subParts = [k.codice, k.id_cliente, k.comune, k.indirizzo].map((x) => (x || "").trim()).filter(Boolean).join(" · ");
          out.push({ id: k.id, label: `${p.codice} · ${p.nome} / ${subParts}` });
        });
      } else {
        out.push({ id: p.id, label: `${p.codice} · ${p.nome}` });
      }
    });
    return out;
  })();

  // ---------- Upload PDF (multiplo) + estrazione AI → coda pending ----------
  const onPickPdf = () => fileRef.current?.click();

  const buildForm = (data) => {
    const ex = data.extracted || {};
    return {
      ...empty,
      numero_fattura: ex.numero_fattura || "",
      data_fattura: ex.data_fattura || "",
      committente: ex.committente || "",
      numero_contratto: ex.numero_contratto || "",
      numero_ordine: ex.numero_ordine || "",
      numero_ses: ex.numero_ses || "",
      data_emissione: ex.data_emissione || new Date().toISOString().slice(0, 10),
      imponibile: ex.imponibile ? String(ex.imponibile) : "",
      iva: ex.iva ? String(ex.iva) : "",
      storage_path: data.storage_path,
      file_name: data.file_name,
      allocazioni: [emptyAlloc()],
    };
  };

  const processFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter(
      (f) => f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf"));
    if (!files.length) return toast.error("Carica uno o più file PDF");
    setUploading(true);
    const added = [];
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/fatture/extract", fd);
        added.push({
          pid: data.bozza_id,
          bozzaId: data.bozza_id,
          blobUrl: URL.createObjectURL(file),
          warning: data.warning || null,
          form: buildForm(data),
        });
      } catch (err) {
        toast.error(`${file.name}: ${formatApiErrorDetail(err.response?.data?.detail) || "estrazione non riuscita"}`);
      }
    }
    setUploading(false);
    if (added.length) {
      setPending((prev) => [...prev, ...added]);
      toast.success(added.length === 1 ? "Dati estratti dal PDF" : `${added.length} fatture pronte da confermare`);
      openPending(added[0]);
    }
  };

  const handleFile = async (e) => {
    const files = e.target.files;
    await processFiles(files);
    e.target.value = "";
  };

  const openPending = async (p) => {
    setForm(p.form);
    setAiWarning(p.warning || null);
    setEditing(null);
    setConfirmingPid(p.pid);
    setOpen(true);
    if (p.blobUrl) {
      setPdfUrl(p.blobUrl);
    } else if (p.bozzaId) {
      setPdfUrl(null);
      try {
        const res = await api.get(`/fatture/bozze/${p.bozzaId}/file`, { responseType: "blob" });
        setPdfUrl(URL.createObjectURL(new Blob([res.data], { type: "application/pdf" })));
      } catch (e) { /* PDF non disponibile */ }
    } else {
      setPdfUrl(null);
    }
  };

  const discardPending = async (pid) => {
    const p = pending.find((x) => x.pid === pid);
    if (p?.blobUrl) URL.revokeObjectURL(p.blobUrl);
    if (p?.bozzaId) { try { await api.delete(`/fatture/bozze/${p.bozzaId}`); } catch (e) { /* già rimossa */ } }
    setPending((prev) => prev.filter((x) => x.pid !== pid));
    if (confirmingPid === pid) { setConfirmingPid(null); setOpen(false); }
  };
  const discardAllPending = async () => {
    await Promise.all(pending.map((p) => {
      if (p.blobUrl) URL.revokeObjectURL(p.blobUrl);
      return p.bozzaId ? api.delete(`/fatture/bozze/${p.bozzaId}`).catch(() => {}) : null;
    }));
    setPending([]);
    setConfirmingPid(null);
    setOpen(false);
  };

  // ---------- Drag & drop ----------
  const onDragEnter = (e) => {
    e.preventDefault();
    if (!isAdmin) return;
    dragCounter.current += 1;
    if (Array.from(e.dataTransfer?.types || []).includes("Files")) setDragOver(true);
  };
  const onDragOver = (e) => { e.preventDefault(); };
  const onDragLeave = (e) => {
    e.preventDefault();
    dragCounter.current -= 1;
    if (dragCounter.current <= 0) { setDragOver(false); dragCounter.current = 0; }
  };
  const onDrop = async (e) => {
    e.preventDefault();
    setDragOver(false);
    dragCounter.current = 0;
    if (!isAdmin) return;
    await processFiles(e.dataTransfer?.files);
  };

  const openNew = () => { if (pdfUrl && !confirmingPid) revokePdf(); setPdfUrl(null); setConfirmingPid(null); setForm({ ...empty, allocazioni: [emptyAlloc()] }); setEditing(null); setAiWarning(null); setOpen(true); };
  const openNewComp = () => { openNew(); setTipo("compensazione"); };

  const tipo = form.is_compensazione ? "compensazione" : form.is_svincolo ? "svincolo" : "ordinaria";
  const setTipo = async (t) => {
    setForm((prev) => ({ ...prev, is_svincolo: t === "svincolo", is_compensazione: t === "compensazione", ...(t === "compensazione" ? { iva: "" } : {}) }));
    if (t === "compensazione" && !editing) {
      try {
        const { data } = await api.get("/fatture/next-compensazione");
        setForm((prev) => (prev.is_compensazione && (!prev.numero_fattura || /^COMP-/.test(prev.numero_fattura)) ? { ...prev, numero_fattura: data.numero } : prev));
      } catch (e) { /* numero assegnato al salvataggio */ }
    }
  };
  const numeroDuplicato = !!form.numero_fattura.trim() && items.some((f) => f.id !== editing
    && (f.numero_fattura || "").trim().toLowerCase() === form.numero_fattura.trim().toLowerCase());
  const openEdit = async (f) => {
    if (pdfUrl && !confirmingPid) revokePdf();
    setPdfUrl(null); setConfirmingPid(null);
    setForm({
      numero_fattura: f.numero_fattura || "", data_fattura: f.data_fattura || "",
      committente: f.committente || "", numero_contratto: f.numero_contratto || "",
      numero_ordine: f.numero_ordine || "", numero_ses: f.numero_ses || "",
      data_emissione: f.data_emissione || "", imponibile: String(f.imponibile ?? ""),
      iva: String(f.iva ?? ""), note: f.note || "", is_svincolo: !!f.is_svincolo, is_compensazione: !!f.is_compensazione,
      storage_path: f.storage_path || null, file_name: f.file_name || null,
      allocazioni: (f.allocazioni?.length ? f.allocazioni : [emptyAlloc()]).map((a) => ({ commessa_id: a.commessa_id, importo: String(a.importo ?? ""), ritenuta: a.ritenuta ? String(a.ritenuta) : "", ritenuta_pct: a.ritenuta_pct ? String(a.ritenuta_pct) : "" })),
    });
    setEditing(f.id);
    setAiWarning(null);
    setOpen(true);
    if (f.storage_path) {
      try {
        const res = await api.get(`/fatture/${f.id}/file`, { responseType: "blob" });
        setPdfUrl(URL.createObjectURL(new Blob([res.data], { type: "application/pdf" })));
      } catch (e) { /* PDF non disponibile: form a colonna singola */ }
    }
  };

  const closeDialog = (v) => {
    if (!v) {
      if (confirmingPid) {
        // persist le modifiche parziali sia in memoria sia nella bozza (sopravvivono al cambio pagina)
        const cur = pending.find((p) => p.pid === confirmingPid);
        setPending((prev) => prev.map((p) => (p.pid === confirmingPid ? { ...p, form } : p)));
        if (cur?.bozzaId) api.put(`/fatture/bozze/${cur.bozzaId}`, form).catch(() => {});
        setConfirmingPid(null);
      } else if (pdfUrl) {
        revokePdf();
      }
      setPdfUrl(null);
      setEditing(null);
    }
    setOpen(v);
  };

  const exportReport = (kind) =>
    downloadFile(`/fatture/export/${kind}`, `fatture.${kind === "excel" ? "xlsx" : "pdf"}`,
      { ids: filteredItems.map((f) => f.id).join(",") })
      .catch(() => toast.error("Errore durante l'esportazione"));

  // ---------- Allocazioni helpers ----------
  const setAlloc = (i, key, val) => setForm((prev) => {
    const next = prev.allocazioni.map((a, idx) => {
      if (idx !== i) return a;
      const na = { ...a, [key]: val };
      const imp = parseFloat(key === "importo" ? val : na.importo) || 0;
      if (key === "importo") {
        // ricalcola ritenuta € dalla % se impostata
        const pct = parseFloat(na.ritenuta_pct);
        if (!isNaN(pct)) na.ritenuta = String(Math.round(imp * pct) / 100);
      } else if (key === "ritenuta_pct") {
        const pct = parseFloat(val);
        na.ritenuta = isNaN(pct) ? "" : String(Math.round(imp * pct) / 100);
      } else if (key === "ritenuta") {
        // aggiorna la % coerente con l'importo € inserito
        const eur = parseFloat(val);
        na.ritenuta_pct = (!isNaN(eur) && imp > 0) ? String(Math.round((eur / imp) * 10000) / 100) : "";
      }
      return na;
    });
    return { ...prev, allocazioni: next };
  });
  const addAlloc = () => setForm((prev) => ({ ...prev, allocazioni: [...prev.allocazioni, emptyAlloc()] }));
  const removeAlloc = (i) => setForm((prev) => ({ ...prev, allocazioni: prev.allocazioni.filter((_, idx) => idx !== i) }));
  const assignRest = (i) => setForm((prev) => {
    const imp = parseFloat(prev.imponibile) || 0;
    const others = prev.allocazioni.reduce((s, a, idx) => (idx === i ? s : s + (parseFloat(a.importo) || 0)), 0);
    const rest = Math.round((imp - others) * 100) / 100;
    const next = prev.allocazioni.map((a, idx) => (idx === i ? { ...a, importo: String(rest > 0 ? rest : 0) } : a));
    return { ...prev, allocazioni: next };
  });

  const imponibileNum = parseFloat(form.imponibile) || 0;
  const assegnato = form.allocazioni.reduce((s, a) => s + (parseFloat(a.importo) || 0), 0);
  const totRitenuta = (form.is_svincolo || form.is_compensazione) ? 0 : form.allocazioni.reduce((s, a) => s + (parseFloat(a.ritenuta) || 0), 0);
  const differenza = Math.round((imponibileNum - assegnato) * 100) / 100;
  const bilanciato = Math.abs(differenza) <= 0.01 && imponibileNum > 0;

  const save = async () => {
    if (!form.numero_fattura.trim()) return toast.error("Il numero fattura è obbligatorio");
    if (numeroDuplicato) return toast.error(`Esiste già un documento con numero «${form.numero_fattura.trim()}»`);
    if (form.is_compensazione) {
      const miss = [["competenza", form.data_emissione], ["n° contratto", form.numero_contratto], ["n° ordine", form.numero_ordine], ["n° SES", form.numero_ses]]
        .filter(([, v]) => !(v || "").trim()).map(([n]) => n);
      if (miss.length) return toast.error(`Compensazione BEF: compila ${miss.join(", ")}`);
    }
    const noRit = form.is_svincolo || form.is_compensazione;
    const alloc = form.allocazioni.filter((a) => a.commessa_id);
    if (!alloc.length) return toast.error("Assegna l'imponibile ad almeno una commessa");
    if (!bilanciato) return toast.error(`La somma delle quote (${euro(assegnato)}) deve essere uguale all'imponibile (${euro(imponibileNum)})`);
    try {
      const payload = {
        numero_fattura: form.numero_fattura, data_fattura: form.data_fattura,
        committente: form.committente, numero_contratto: form.numero_contratto,
        numero_ordine: form.numero_ordine, numero_ses: form.numero_ses,
        data_emissione: form.data_emissione, imponibile: imponibileNum,
        iva: form.is_compensazione ? 0 : (parseFloat(form.iva) || 0), note: form.note,
        is_svincolo: !!form.is_svincolo, is_compensazione: !!form.is_compensazione,
        storage_path: form.storage_path, file_name: form.file_name,
        allocazioni: alloc.map((a) => ({
          commessa_id: a.commessa_id, importo: parseFloat(a.importo) || 0,
          ritenuta: noRit ? 0 : (parseFloat(a.ritenuta) || 0),
          ritenuta_pct: noRit ? 0 : (parseFloat(a.ritenuta_pct) || 0),
        })),
      };
      if (editing) await api.put(`/fatture/${editing}`, payload);
      else await api.post("/fatture", payload);
      const nome = form.is_compensazione ? "Compensazione BEF" : "Fattura";
      toast.success(editing ? `${nome} aggiornata` : `${nome} registrata`);
      load();
      if (confirmingPid) {
        const cur = pending.find((x) => x.pid === confirmingPid);
        if (cur?.blobUrl) URL.revokeObjectURL(cur.blobUrl);
        if (cur?.bozzaId) { try { await api.delete(`/fatture/bozze/${cur.bozzaId}`); } catch (e) { /* già rimossa */ } }
        const rest = pending.filter((x) => x.pid !== confirmingPid);
        setPending(rest);
        setConfirmingPid(null);
        if (rest.length) { openPending(rest[0]); return; }
        setPdfUrl(null); setOpen(false);
        return;
      }
      if (pdfUrl) revokePdf();
      setPdfUrl(null); setEditing(null); setOpen(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Salvataggio non riuscito");
    }
  };

  const remove = async (id) => { await api.delete(`/fatture/${id}`); toast.success("Fattura eliminata"); load(); };

  const viewPdf = async (f) => {
    try {
      const res = await api.get(`/fatture/${f.id}/file`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error("PDF non disponibile");
    }
  };

  const filteredItems = items.filter((f) => {
    const q = query.trim().toLowerCase();
    const matchQ = !q || [f.numero_fattura, f.committente, f.numero_ses, f.numero_contratto, f.numero_ordine, f.note]
      .some((v) => String(v || "").toLowerCase().includes(q));
    const matchC = fCommessa.length === 0 || (f.allocazioni || []).some((a) => fCommessa.includes(a.commessa_id));
    const matchFrom = !dateFrom || (f.data_emissione || "") >= dateFrom;
    const matchTo = !dateTo || (f.data_emissione || "") <= dateTo;
    const matchFF = !fattFrom || (f.data_fattura || "") >= fattFrom;
    const matchFT = !fattTo || (f.data_fattura || "") <= fattTo;
    const matchComm = fCommittente.length === 0 || fCommittente.includes(f.committente || "");
    const matchTipo = fTipo.length === 0 || fTipo.includes(f.is_compensazione ? "compensazione" : f.is_svincolo ? "svincolo" : "ordinaria");
    return matchQ && matchC && matchFrom && matchTo && matchFF && matchFT && matchComm && matchTipo;
  });

  const totImponibile = filteredItems.reduce((s, f) => s + (f.imponibile || 0), 0);
  const totIva = filteredItems.reduce((s, f) => s + (f.iva || 0), 0);
  const totRitenute = filteredItems.reduce((s, f) => f.is_svincolo
    ? s - (f.imponibile || 0)
    : s + (f.allocazioni || []).reduce((a, x) => a + (x.ritenuta || 0), 0), 0);

  const columns = [
    { id: "numero_fattura", label: "N° Fattura", width: 160, sortable: true, sortValue: (f) => f.numero_fattura || "",
      cell: (f) => (
        <span className="inline-flex flex-col">
          <span className="font-semibold text-slate-900">{f.numero_fattura || "—"}</span>
          {f.is_compensazione && <span data-testid={`fattura-comp-badge-${f.id}`} className="text-[10px] font-semibold uppercase tracking-wide text-violet-700">Compensazione BEF</span>}
        </span>) },
    { id: "data_fattura", label: "Data Fattura", width: 120, sortable: true, sortValue: (f) => f.data_fattura || "",
      cell: (f) => <span className="text-slate-600 tabular-nums">{formatDate(f.data_fattura)}</span> },
    { id: "data_emissione", label: "Data Competenza Lavori", width: 170, sortable: true, sortValue: (f) => f.data_emissione || "",
      cell: (f) => <span className="text-slate-600 tabular-nums">{formatDate(f.data_emissione)}</span> },
    { id: "committente", label: "Committente", width: 200, sortable: true, sortValue: (f) => f.committente || "",
      cell: (f) => <span className="text-slate-700">{f.committente || "—"}</span> },
    { id: "numero_ses", label: "N° SES", width: 120, sortable: true, sortValue: (f) => f.numero_ses || "",
      cell: (f) => <span className="text-slate-600">{f.numero_ses || "—"}</span> },
    { id: "commesse", label: "Commesse", width: 300, sortable: false, cellClassName: "whitespace-normal align-top",
      cell: (f) => (
        <div className="flex flex-wrap gap-1 py-0.5">
          {(f.allocazioni || []).map((a, i) => (
            <Badge key={i} variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 font-normal" title={commessaLabel(a.commessa_id)}>
              {(commesse.find((c) => c.id === a.commessa_id)?.nome) || "?"} · {euro(a.importo)}
            </Badge>
          ))}
        </div>) },
    { id: "imponibile", label: "Imponibile", width: 130, align: "right", sortable: true, sortValue: (f) => f.imponibile || 0,
      cell: (f) => <span className="font-semibold text-emerald-700">{euro(f.imponibile)}</span> },
    { id: "iva", label: "IVA", width: 110, align: "right", sortable: true, sortValue: (f) => f.iva || 0,
      cell: (f) => <span className="text-slate-500 tabular-nums">{euro(f.iva)}</span> },
    { id: "ritenuta", label: "Ritenuta a garanzia", width: 150, align: "right", sortable: true,
      sortValue: (f) => (f.allocazioni || []).reduce((s, a) => s + (a.ritenuta || 0), 0),
      cell: (f) => {
        const rit = (f.allocazioni || []).reduce((s, a) => s + (a.ritenuta || 0), 0);
        if (f.is_compensazione) return <span className="text-slate-500 text-xs">n/a</span>;
        if (f.is_svincolo) return <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 font-normal" title="Fattura di svincolo ritenuta" data-testid={`fattura-svincolo-badge-${f.id}`}><ShieldCheck className="h-3 w-3 mr-1" />Svincolo{f.imponibile ? ` ${euro(f.imponibile)}` : ""}</Badge>;
        return rit > 0
          ? <span className="font-semibold text-amber-700 inline-flex items-center gap-1 tabular-nums" data-testid={`fattura-ritenuta-${f.id}`}><ShieldCheck className="h-3 w-3" />{euro(rit)}</span>
          : <span className="text-slate-300">—</span>;
      } },
    { id: "pdf", label: "PDF", width: 70, align: "center", sortable: false,
      cell: (f) => f.storage_path ? (
        <Button variant="ghost" size="icon" data-testid={`view-pdf-${f.id}`} title="Apri PDF" onClick={() => viewPdf(f)} className="h-8 w-8 text-slate-500 hover:text-rose-600"><FileText className="h-4 w-4" /></Button>
      ) : <span className="text-slate-300 text-xs">—</span> },
    { id: "azioni", label: "Azioni", width: 110, align: "right", sortable: false,
      cell: (f) => isAdmin ? (
        <div className="flex items-center justify-end gap-0.5">
          <Button variant="ghost" size="icon" data-testid={`edit-fattura-${f.id}`} onClick={() => openEdit(f)} className="h-8 w-8 text-slate-500 hover:text-blue-700"><Pencil className="h-4 w-4" /></Button>
          <ConfirmDelete title="Eliminare fattura?" description="La fattura verrà rimossa (il PDF resta archiviato)." actionTestid={`confirm-delete-fattura-${f.id}`} onConfirm={() => remove(f.id)}>
            <Button variant="ghost" size="icon" data-testid={`delete-fattura-${f.id}`} className="h-8 w-8 text-slate-500 hover:text-rose-600"><Trash2 className="h-4 w-4" /></Button>
          </ConfirmDelete>
        </div>) : <span className="text-slate-300 text-xs">—</span> },
  ];

  return (
    <>
      <input ref={fileRef} type="file" accept="application/pdf,.pdf" multiple onChange={handleFile} className="hidden" data-testid="fattura-pdf-input" />

      <div className="flex-1 overflow-auto px-6 py-6 lg:px-8 space-y-5 relative"
        onDragEnter={onDragEnter} onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}>

        {dragOver && (
          <div data-testid="fattura-dropzone" className="absolute inset-3 z-40 rounded-xl border-2 border-dashed border-blue-500 bg-blue-50/90 backdrop-blur-sm flex flex-col items-center justify-center pointer-events-none">
            <FileUp className="h-10 w-10 text-blue-600 mb-3" />
            <p className="text-blue-800 font-heading font-semibold text-lg">Rilascia qui le fatture PDF</p>
            <p className="text-blue-600 text-sm mt-1">Puoi trascinarne anche più di una — l'AI estrarrà i dati</p>
          </div>
        )}

        <PageHeader title="Fatture" subtitle="Carica o trascina una o più fatture PDF: i dati vengono estratti dall'AI e l'imponibile ripartito sulle commesse">
          <Button variant="outline" size="sm" data-testid="fatture-export-excel" onClick={() => exportReport("excel")}
            disabled={items.length === 0} title={items.length === 0 ? "Nessuna fattura da esportare" : "Esporta in Excel"}>
            <FileSpreadsheet className="h-4 w-4 mr-1.5" /> Excel
          </Button>
          <Button variant="outline" size="sm" data-testid="fatture-export-pdf" onClick={() => exportReport("pdf")}
            disabled={items.length === 0} title={items.length === 0 ? "Nessuna fattura da esportare" : "Esporta in PDF"}>
            <FileText className="h-4 w-4 mr-1.5" /> PDF
          </Button>
          {isAdmin && (
            <>
              <Button data-testid="upload-fattura-button" onClick={onPickPdf} disabled={uploading} className="bg-blue-700 hover:bg-blue-800 active:scale-[0.98] transition-transform">
                {uploading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <FileUp className="h-4 w-4 mr-1.5" />}
                {uploading ? "Analisi PDF…" : "Carica fatture (PDF)"}
              </Button>
              <Button variant="outline" data-testid="new-fattura-manual-button" onClick={openNew}>
                <Plus className="h-4 w-4 mr-1.5" /> Manuale
              </Button>
              <Button variant="outline" data-testid="new-compensazione-button" onClick={openNewComp} className="text-violet-700 border-violet-200 hover:bg-violet-50">
                <Scale className="h-4 w-4 mr-1.5" /> Compensazione BEF
              </Button>
            </>
          )}
        </PageHeader>

        {pending.length > 0 && (
          <div data-testid="fatture-pending-bar" className="rounded-md border border-blue-200 bg-blue-50/70 p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-blue-800 flex items-center gap-1.5">
                <Sparkles className="h-4 w-4" /> {pending.length} {pending.length > 1 ? "fatture da confermare" : "fattura da confermare"}
              </span>
              <Button variant="ghost" size="sm" data-testid="pending-discard-all" onClick={discardAllPending} className="h-7 text-xs text-slate-500 hover:text-rose-600">Scarta tutte</Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {pending.map((p) => (
                <div key={p.pid} className="flex items-center gap-1 bg-white border border-blue-200 rounded-full pl-3 pr-1 py-1 text-sm shadow-sm">
                  <button data-testid={`pending-open-${p.pid}`} onClick={() => openPending(p)} className="text-blue-700 hover:underline flex items-center gap-1.5 max-w-[220px] truncate">
                    <FileText className="h-3.5 w-3.5 flex-shrink-0" /> <span className="truncate">{p.form.file_name || "PDF"}</span>
                  </button>
                  <button data-testid={`pending-discard-${p.pid}`} onClick={() => discardPending(p.pid)} title="Scarta" className="text-slate-400 hover:text-rose-600 flex-shrink-0"><X className="h-3.5 w-3.5" /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        <Card className="rounded-md border-slate-200 shadow-sm overflow-hidden">
          {items.length === 0 ? (
            <div className="py-16 flex flex-col items-center text-center">
              <div className="h-14 w-14 rounded-lg bg-slate-100 flex items-center justify-center mb-4"><ReceiptText className="h-6 w-6 text-slate-400" /></div>
              <p className="text-slate-600 font-medium">Nessuna fattura</p>
              <p className="text-sm text-slate-400 mt-1 mb-4 max-w-md">Trascina qui una fattura in PDF o usa il pulsante: i dati verranno estratti automaticamente e potrai ripartire l'imponibile su una o più commesse.</p>
              {isAdmin && <Button variant="outline" onClick={onPickPdf}><FileUp className="h-4 w-4 mr-1.5" /> Carica fatture (PDF)</Button>}
            </div>
          ) : (
            <>
              <div className="border-b border-slate-100 bg-slate-50/60">
                <div className="flex items-center justify-end px-3 pt-2">
                  <span className="text-xs text-slate-400">{filteredItems.length} / {items.length} fatture</span>
                </div>
                <div className="flex flex-wrap items-end gap-3 px-3 pb-3">
                  <div className="relative w-56 flex-shrink-0">
                    <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <Input data-testid="fatture-search" value={query} onChange={(e) => setQuery(e.target.value)}
                      placeholder="Cerca n° fattura, committente, SES, ordine…" className="pl-9 bg-white" />
                  </div>
                  <div className="w-48 flex-shrink-0">
                    <Label className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">Committente</Label>
                    <div className="mt-1">
                      <MultiSelect testid="fatture-filter-committente" triggerClassName="w-full" placeholder="Tutti i committenti"
                        value={fCommittente} onChange={setFCommittente}
                        options={Array.from(new Set(items.map((f) => f.committente).filter(Boolean))).sort((a, b) => a.localeCompare(b, "it")).map((c) => ({ value: c, label: c }))} />
                    </div>
                  </div>
                  <div className="w-40 flex-shrink-0">
                    <Label className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">Tipo</Label>
                    <div className="mt-1"><MultiSelect testid="fatture-filter-tipo" triggerClassName="w-full" placeholder="Tutti i tipi" value={fTipo} onChange={setFTipo} options={TIPI} /></div>
                  </div>
                  <div className="w-56 flex-shrink-0">
                    <Label className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">Commesse</Label>
                    <div className="mt-1">
                      <MultiSelect testid="fatture-filter-commessa" triggerClassName="w-full" placeholder="Tutte le commesse"
                        value={fCommessa} onChange={setFCommessa}
                        options={allocatable.map((c) => ({ value: c.id, label: c.label }))} />
                    </div>
                  </div>
                  <div className="w-36 flex-shrink-0">
                    <Label className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">Fattura da</Label>
                    <div className="mt-1"><DatePickerField testid="fatture-filter-fattura-da" value={fattFrom} onChange={setFattFrom} placeholder="Da…" /></div>
                  </div>
                  <div className="w-36 flex-shrink-0">
                    <Label className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">Fattura a</Label>
                    <div className="mt-1"><DatePickerField testid="fatture-filter-fattura-a" value={fattTo} onChange={setFattTo} placeholder="A…" /></div>
                  </div>
                  <div className="w-36 flex-shrink-0">
                    <Label className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">Competenza da</Label>
                    <div className="mt-1"><DatePickerField testid="fatture-filter-da" value={dateFrom} onChange={setDateFrom} placeholder="Da…" /></div>
                  </div>
                  <div className="w-36 flex-shrink-0">
                    <Label className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">Competenza a</Label>
                    <div className="mt-1"><DatePickerField testid="fatture-filter-a" value={dateTo} onChange={setDateTo} placeholder="A…" /></div>
                  </div>
                  {hasFilter && (
                    <Button variant="ghost" size="sm" data-testid="fatture-clear-filters" onClick={resetFilters} className="h-10 flex-shrink-0 text-slate-500 hover:text-rose-600">
                      <X className="h-4 w-4 mr-1" /> Azzera
                    </Button>
                  )}
                </div>
              </div>
              {filteredItems.length === 0 ? (
                <div className="py-16 text-center text-sm text-slate-400">Nessuna fattura corrisponde ai filtri.</div>
              ) : (
                <>
                  <DataTable tableKey="fatture" columns={columns} rows={filteredItems} rowKey={(f) => f.id}
                    rowTestId={(f) => `fattura-row-${f.id}`} containerClassName="max-h-[calc(100vh-340px)]"
                    initialSort={{ key: "data_emissione", dir: "desc" }} />
                  <div data-testid="fatture-totali" className="flex flex-wrap items-center justify-end gap-x-8 gap-y-1 px-4 py-3 border-t border-slate-200 bg-slate-50 text-sm">
                    <span className="text-slate-500">Righe visualizzate: <strong className="text-slate-800">{filteredItems.length}</strong></span>
                    <span className="text-slate-500">Totale imponibile: <strong className="text-emerald-700 tabular-nums" data-testid="fatture-tot-imponibile">{euro(totImponibile)}</strong></span>
                    <span className="text-slate-500">Totale IVA: <strong className="text-slate-700 tabular-nums" data-testid="fatture-tot-iva">{euro(totIva)}</strong></span>
                    <span className="text-slate-500">Totale ritenute a garanzia: <strong className={`tabular-nums ${totRitenute < 0 ? "text-rose-600" : "text-amber-700"}`} data-testid="fatture-tot-ritenute">{euro(totRitenute)}</strong></span>
                  </div>
                </>
              )}
            </>
          )}
        </Card>
      </div>

      <Dialog open={open} onOpenChange={closeDialog}>
        <DialogContent className={pdfUrl ? "sm:max-w-5xl max-h-[92vh] overflow-hidden" : "sm:max-w-3xl max-h-[92vh] overflow-y-auto overflow-x-hidden"}>
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              {editing ? (form.is_compensazione ? "Modifica compensazione BEF" : "Modifica fattura") : (form.is_compensazione ? "Nuova compensazione BEF" : "Nuova fattura")}
              {form.file_name && <Badge variant="outline" className="bg-rose-50 text-rose-600 border-rose-200 font-normal"><FileText className="h-3 w-3 mr-1" />{form.file_name}</Badge>}
            </DialogTitle>
            <DialogDescription>{form.is_compensazione ? "BEF non emesso per recuperare anticipi: senza IVA e senza ritenuta, concorre al Riconosciuto." : "Verifica i dati estratti e ripartisci l'imponibile sulle commesse."}</DialogDescription>
          </DialogHeader>

          <div className={pdfUrl ? "grid grid-cols-2 gap-4 min-h-0 min-w-0" : "min-w-0"}>
            {pdfUrl && (
              <div className="border border-slate-200 rounded-md overflow-hidden bg-slate-100" data-testid="fattura-pdf-preview">
                <iframe title="Anteprima fattura" src={pdfUrl} className="w-full h-[68vh]" />
              </div>
            )}
            <div className={pdfUrl ? "overflow-y-auto max-h-[68vh] pr-1 min-w-0" : "min-w-0"}>
              {aiWarning && (
                <div data-testid="fattura-ai-warning" className="flex items-center gap-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800 mb-3">
                  <Sparkles className="h-4 w-4 flex-shrink-0" /> {aiWarning}
                </div>
              )}
              <div className="space-y-4 py-1">
                <div data-testid="fattura-tipo-selector" className="inline-flex rounded-md border border-slate-200 bg-slate-50 p-0.5">
                  {TIPI.map((t) => (
                    <button key={t.value} type="button" data-testid={`fattura-tipo-${t.value}`} onClick={() => setTipo(t.value)}
                      className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${tipo === t.value ? (t.value === "compensazione" ? "bg-violet-600 text-white" : t.value === "svincolo" ? "bg-amber-500 text-white" : "bg-blue-700 text-white") : "text-slate-600 hover:bg-white"}`}>
                      {t.label}
                    </button>
                  ))}
                </div>
                <div className={form.is_compensazione ? "" : "grid grid-cols-2 gap-3"}>
                  <div><Label className="text-slate-700 text-sm">{form.is_compensazione ? "N° Compensazione *" : "N° Fattura *"}</Label><Input data-testid="fattura-numero-input" value={form.numero_fattura} onChange={(e) => setForm({ ...form, numero_fattura: e.target.value })} className={`mt-1.5 ${numeroDuplicato ? "border-rose-400 focus-visible:ring-rose-500" : ""}`} placeholder={form.is_compensazione ? "COMP-2026-001" : "2026/0042"} />
                    {numeroDuplicato && <p data-testid="fattura-numero-duplicato" className="text-xs text-rose-600 mt-1">Numero già registrato: scegline un altro.</p>}
                  </div>
                  {!form.is_compensazione && <div><Label className="text-slate-700 text-sm">Data fattura</Label><div className="mt-1.5"><DatePickerField testid="fattura-data-input" value={form.data_fattura} onChange={(v) => setForm({ ...form, data_fattura: v })} /></div></div>}
                </div>
                <div><Label className="text-slate-700 text-sm">Committente (destinatario)</Label>
                  <div className="mt-1.5">
                    <Combobox testid="fattura-committente-input" value={form.committente || ""}
                      onChange={(v) => setForm({ ...form, committente: v })}
                      placeholder="Seleziona committente"
                      options={(() => {
                        const names = committenti.map((c) => c.nome);
                        if (form.committente && !names.includes(form.committente)) names.unshift(form.committente);
                        return names.map((n) => ({ value: n, label: n }));
                      })()} />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div><Label className="text-slate-700 text-sm">N° Contratto{form.is_compensazione && " *"}</Label><Input data-testid="fattura-contratto-input" value={form.numero_contratto} onChange={(e) => setForm({ ...form, numero_contratto: e.target.value })} className="mt-1.5" /></div>
                  <div><Label className="text-slate-700 text-sm">N° Ordine{form.is_compensazione && " *"}</Label><Input data-testid="fattura-ordine-input" value={form.numero_ordine} onChange={(e) => setForm({ ...form, numero_ordine: e.target.value })} className="mt-1.5" /></div>
                  <div><Label className="text-slate-700 text-sm">N° SES{form.is_compensazione && " *"}</Label><Input data-testid="fattura-ses-input" value={form.numero_ses} onChange={(e) => setForm({ ...form, numero_ses: e.target.value })} className="mt-1.5" /></div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div><Label className="text-slate-700 text-sm">{form.is_compensazione ? "Competenza *" : "Data Competenza Lavori"}</Label><div className="mt-1.5"><DatePickerField testid="fattura-emissione-input" value={form.data_emissione} onChange={(v) => setForm({ ...form, data_emissione: v })} /></div></div>
                  <div><Label className="text-slate-700 text-sm">{form.is_compensazione ? "Importo €" : "Imponibile €"}</Label><Input data-testid="fattura-imponibile-input" type="number" step="0.01" value={form.imponibile} onChange={(e) => setForm({ ...form, imponibile: e.target.value })} className="mt-1.5" /></div>
                  {!form.is_compensazione && <div><Label className="text-slate-700 text-sm">IVA €</Label><Input data-testid="fattura-iva-input" type="number" step="0.01" value={form.iva} onChange={(e) => setForm({ ...form, iva: e.target.value })} className="mt-1.5" /></div>}
                </div>

                {/* Ripartizione */}
                <div className="rounded-md border border-slate-200 p-3">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-slate-700 text-sm font-semibold">{form.is_svincolo ? "Svincolo per commessa" : form.is_compensazione ? "Ripartizione compensazione sulle commesse" : "Ripartizione imponibile sulle commesse"}</Label>
                    <Button type="button" variant="outline" size="sm" data-testid="add-allocazione-button" onClick={addAlloc} className="h-7 text-xs"><Plus className="h-3.5 w-3.5 mr-1" /> Commessa</Button>
                  </div>
                  <div className="space-y-2">
                    {form.allocazioni.map((a, i) => (
                      <div key={i} className="rounded-md border border-slate-100 p-2" data-testid={`allocazione-row-${i}`}>
                        <div className="flex items-center gap-2 w-full">
                          <Combobox testid={`allocazione-commessa-${i}`} value={a.commessa_id}
                            onChange={(v) => setAlloc(i, "commessa_id", v)}
                            options={allocatable.map((c) => ({ value: c.id, label: c.label }))}
                            placeholder="Seleziona commessa" searchPlaceholder="Cerca commessa…"
                            emptyText="Nessuna commessa" wrap className="flex-1 min-w-0 h-9"
                            contentClassName="w-[min(90vw,42rem)] max-w-[90vw]" />
                          <Input data-testid={`allocazione-importo-${i}`} type="number" step="0.01" placeholder={form.is_svincolo ? "€ svincolo" : "€"} value={a.importo}
                            onChange={(e) => setAlloc(i, "importo", e.target.value)} className="w-24 flex-shrink-0 text-right" />
                          <Button type="button" variant="ghost" size="sm" data-testid={`allocazione-rest-${i}`} onClick={() => assignRest(i)} title="Assegna importo restante" className="h-8 flex-shrink-0 text-xs text-blue-700 px-2">Resto</Button>
                          <Button type="button" variant="ghost" size="icon" data-testid={`allocazione-remove-${i}`} onClick={() => removeAlloc(i)} disabled={form.allocazioni.length <= 1} className="h-8 w-8 flex-shrink-0 text-slate-400 hover:text-rose-600"><X className="h-4 w-4" /></Button>
                        </div>
                        {!form.is_svincolo && !form.is_compensazione && (
                          <div className="flex items-center gap-2 mt-2 pl-1 text-xs text-slate-500">
                            <ShieldCheck className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />
                            <span className="flex-shrink-0">Ritenuta a garanzia:</span>
                            <Input data-testid={`allocazione-ritenuta-pct-${i}`} type="number" step="0.01" placeholder="%" value={a.ritenuta_pct}
                              onChange={(e) => setAlloc(i, "ritenuta_pct", e.target.value)} className="w-16 h-8 text-right" />
                            <span>%</span>
                            <Input data-testid={`allocazione-ritenuta-${i}`} type="number" step="0.01" placeholder="€" value={a.ritenuta}
                              onChange={(e) => setAlloc(i, "ritenuta", e.target.value)} className="w-24 h-8 text-right" />
                            <span>€</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center justify-between mt-3 pt-2 border-t border-slate-100 text-sm" data-testid="allocazione-summary">
                    <span className="text-slate-500">Assegnato <strong className="text-slate-800 tabular-nums">{euro(assegnato)}</strong> / Imponibile <strong className="text-slate-800 tabular-nums">{euro(imponibileNum)}</strong></span>
                    <span className={`font-semibold tabular-nums ${bilanciato ? "text-emerald-600" : "text-rose-600"}`}>
                      {bilanciato ? "Bilanciato ✓" : `Differenza ${euro(differenza)}`}
                    </span>
                  </div>
                  {!form.is_svincolo && !form.is_compensazione && totRitenuta > 0 && (
                    <div className="flex items-center justify-end mt-1 text-xs text-amber-700" data-testid="ritenuta-summary">
                      <ShieldCheck className="h-3.5 w-3.5 mr-1" /> Ritenuta a garanzia totale: <strong className="tabular-nums ml-1">{euro(totRitenuta)}</strong>
                    </div>
                  )}
                </div>

                <div><Label className="text-slate-700 text-sm">Note</Label><Input data-testid="fattura-note-input" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="mt-1.5" /></div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => closeDialog(false)}>Annulla</Button>
            <Button data-testid="save-fattura-button" onClick={save} disabled={!bilanciato || numeroDuplicato} className="bg-blue-700 hover:bg-blue-800">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
